import React, { useState, useEffect } from 'react';
import { AiOutlineClose, AiOutlineMenu } from 'react-icons/ai';
import { FiChevronDown } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import TitleLogo from '../assets/shellsafe_logo.png';

/*
  navbar.js

  Contributor notes:
  - To add/remove top-level pages, update the links in both the desktop and
    mobile nav sections (look for the `Link to="/..."` entries).
  - If you add new translation keys, update `src/locales/en.json` (and other
    locale files) accordingly.
*/

const Navbar = () => {
  const [nav, setNav] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);

  // Get user data from localStorage
  useEffect(() => {
    const userEmail = localStorage.getItem("email");
    const userRole = localStorage.getItem("role");
    setUser(userEmail);
    setRole(userRole);
  }, []);

  const handleNav = () => {
    setNav(!nav);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
    setNav(false);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const getFlagForLanguage = () => {
    switch (i18n.language) {
      case 'es': return 'https://flagcdn.com/w40/es.png';
      case 'pt': return 'https://flagcdn.com/w40/pt.png';
      default: return 'https://flagcdn.com/w40/gb.png';
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleSignOut = () => {
    localStorage.clear(); // Clear all localStorage data
    window.location.href = "/login"; // Redirect to login page
  };

  return (
    <div className="fixed top-0 left-0 w-full h-20 flex items-center px-4 md:px-12 bg-gradient-to-r from-teal-600 to-blue-800 z-50">
      <div className="flex items-center">
        <Link to="/">
          <img
            className="h-10 md:h-12 w-auto transition-transform duration-300 hover:scale-105"
            src={TitleLogo}
            alt="ShellSafe"
          />
        </Link>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex flex-1 justify-end items-center">
        <div className="flex space-x-6 lg:space-x-12">
          <Link
            to="/"
            className={`no-underline text-white text-sm lg:text-base font-light hover:opacity-80 relative ${isActive('/') ? 'active-link' : ''}`}
          >
            {t("navbar.home")}
            {isActive('/') && <span className="absolute bottom-[-8px] left-0 w-full h-0.5 bg-white" />}
          </Link>
          <Link
            to="/about"
            className={`no-underline text-white text-sm lg:text-base font-light hover:opacity-80 relative ${isActive('/about') ? 'active-link' : ''}`}
          >
            {t("navbar.about")}
            {isActive('/about') && <span className="absolute bottom-[-8px] left-0 w-full h-0.5 bg-white" />}
          </Link>
          <Link
            to="/research"
            className={`no-underline text-white text-sm lg:text-base font-light hover:opacity-80 relative ${isActive('/research') ? 'active-link' : ''}`}
          >
            {t("navbar.research")}
            {isActive('/research') && <span className="absolute bottom-[-8px] left-0 w-full h-0.5 bg-white" />}
          </Link>
          <Link
            to="/map"
            className={`no-underline text-white text-sm lg:text-base font-light hover:opacity-80 relative ${isActive('/map') ? 'active-link' : ''}`}
          >
            {t("navbar.map")}
            {isActive('/map') && <span className="absolute bottom-[-8px] left-0 w-full h-0.5 bg-white" />}
          </Link>

          {/* Conditionally render 'Data Input' for admin */}
          {role === "admin" && (
            <Link
              to="/data-input"
              className="no-underline text-white text-sm lg:text-base font-light hover:opacity-80 relative"
            >
              {t("navbar.data_input")}
            </Link>
          )}

          {/* Show logged-in user's email and Sign-out button */}
          {user ? (
            <div className="text-white flex items-center">
              <button
                onClick={handleSignOut}
                className="text-white text-sm lg:text-base font-light hover:opacity-80 relative"
              >
                {t("navbar.sign_out")}
              </button>
            </div>
          ) : (
            // Show login button if the user is not logged in
            <Link
              to="/login"
              className="text-white text-sm lg:text-base font-light hover:opacity-80 relative"
            >
              {t("navbar.log_in")}
            </Link>
          )}
        </div>

        {/* Language Switcher */}
        <div className="relative flex items-center ml-6 lg:ml-12">
          <div onClick={handleClick} className="flex items-center cursor-pointer hover:opacity-80">
            <img
              src={getFlagForLanguage()}
              className="w-5 h-5 object-cover rounded-full"
              alt="Language"
            />
            <FiChevronDown className="ml-1 text-white w-3 h-3" />
          </div>

          {isOpen && (
            <div
              onMouseLeave={handleMouseLeave}
              className="absolute right-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-[100] overflow-hidden"
            >
              <button onClick={() => changeLanguage('en')} className="w-full px-4 py-2 text-left hover:bg-gray-100 text-gray-800 flex items-center gap-2">
                <img src="https://flagcdn.com/w40/gb.png" className="w-5 h-4" alt={t("common.english")} /> {t("common.english")}
              </button>
              <button onClick={() => changeLanguage('pt')} className="w-full px-4 py-2 text-left hover:bg-gray-100 text-gray-800 flex items-center gap-2">
                <img src="https://flagcdn.com/w40/pt.png" className="w-5 h-4" alt={t("common.portuguese")} /> {t("common.portuguese")}
              </button>
              <button onClick={() => changeLanguage('es')} className="w-full px-4 py-2 text-left hover:bg-gray-100 text-gray-800 flex items-center gap-2">
                <img src="https://flagcdn.com/w40/es.png" className="w-5 h-4" alt={t("common.spanish")} /> {t("common.spanish")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Button */}
      <div className="md:hidden flex items-center ml-auto">
        <div className="relative mr-4">
          <div onClick={handleClick} className="flex items-center cursor-pointer hover:opacity-80">
            <img
              src={getFlagForLanguage()}
              className="w-5 h-5 object-cover rounded-full"
              alt="Language"
            />
          </div>

          {isOpen && (
            <div
              onMouseLeave={handleMouseLeave}
              className="absolute right-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-[100] overflow-hidden"
            >
              <button onClick={() => changeLanguage('en')} className="w-full px-4 py-2 text-left hover:bg-gray-100 text-gray-800 flex items-center gap-2">
                <img src="https://flagcdn.com/w40/gb.png" className="w-5 h-4" alt={t("common.english")} /> {t("common.english")}
              </button>
              <button onClick={() => changeLanguage('pt')} className="w-full px-4 py-2 text-left hover:bg-gray-100 text-gray-800 flex items-center gap-2">
                <img src="https://flagcdn.com/w40/pt.png" className="w-5 h-4" alt={t("common.portuguese")} /> {t("common.portuguese")}
              </button>
              <button onClick={() => changeLanguage('es')} className="w-full px-4 py-2 text-left hover:bg-gray-100 text-gray-800 flex items-center gap-2">
                <img src="https://flagcdn.com/w40/es.png" className="w-5 h-4" alt={t("common.spanish")} /> {t("common.spanish")}
              </button>
            </div>
          )}
        </div>

        <button onClick={handleNav} className="text-white focus:outline-none">
          {!nav ? <AiOutlineMenu size={24} /> : <AiOutlineClose size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {nav && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setNav(false)} />
      )}

      {/* Mobile Menu Content */}
      <div
        className={`fixed top-0 right-0 h-full w-3/4 max-w-xs bg-[#004e89] shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${nav ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <Link to="/" onClick={() => setNav(false)}>
            <img className="h-10 w-auto" src={TitleLogo} alt="ShellSafe" />
          </Link>
          <button onClick={() => setNav(false)} className="text-white p-2">
            <AiOutlineClose size={24} />
          </button>
        </div>

        <nav className="p-4">
          <ul className="space-y-2">
            <li>
              <Link
                to="/"
                className={`no-underline block px-4 py-3 rounded-md ${isActive('/')
                    ? 'bg-blue-700 text-white font-medium'
                    : 'text-gray-200 hover:bg-blue-800 hover:text-white'
                  }`}
                onClick={() => setNav(false)}
              >
                {t("navbar.home")}
                {isActive('/') && <span className="block h-0.5 bg-white mt-1 w-1/2" />}
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                className={`no-underline block px-4 py-3 rounded-md ${isActive('/about')
                    ? 'bg-blue-700 text-white font-medium'
                    : 'text-gray-200 hover:bg-blue-800 hover:text-white'
                  }`}
                onClick={() => setNav(false)}
              >
                {t("navbar.about")}
                {isActive('/about') && <span className="block h-0.5 bg-white mt-1 w-1/2" />}
              </Link>
            </li>
            <li>
              <Link
                to="/research"
                className={`no-underline block px-4 py-3 rounded-md ${isActive('/research')
                    ? 'bg-blue-700 text-white font-medium'
                    : 'text-gray-200 hover:bg-blue-800 hover:text-white'
                  }`}
                onClick={() => setNav(false)}
              >
                {t("navbar.research")}
                {isActive('/research') && <span className="block h-0.5 bg-white mt-1 w-1/2" />}
              </Link>
            </li>
            <li>
              <Link
                to="/map"
                className={`no-underline block px-4 py-3 rounded-md ${isActive('/map')
                    ? 'bg-blue-700 text-white font-medium'
                    : 'text-gray-200 hover:bg-blue-800 hover:text-white'
                  }`}
                onClick={() => setNav(false)}
              >
                {t("navbar.map")}
                {isActive('/map') && <span className="block h-0.5 bg-white mt-1 w-1/2" />}
              </Link>
            </li>
            {/* Conditionally render 'Data Input' for admin */}
            {role === "admin" && (
              <li>
                <Link
                  to="/data-input"
                  className="no-underline block px-4 py-3 rounded-md text-white"
                  onClick={() => setNav(false)}
                >
                  {t("navbar.data_input")}
                </Link>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Navbar;
